import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import GalleryMultiselect, { GallerySelectItem } from '../../../common/GalleryMultiselect';
import { fetchGallections } from '../../../redux/actions/gallections';
import { useSubmitField } from '../../../hooks/useSubmitField';

import s from './StashFolderSelect.scss';

interface Props {
  className?: string;
}

const StashFolderSelect: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<GallerySelectItem[] | null>(null);
  const dispatch = useDispatch();

  const {
    id,
    field: { onChange, value },
  } = useSubmitField('originalStashFolderId', {
    initAdapter: ({ originalsDelivery }) => originalsDelivery?.folderId,
    submitAdapter: ({ originalStashFolderId }) => ({
      folderid: originalStashFolderId,
    }),
  });
  const initialValueRef = useRef<number | undefined>(value);

  const handleChange = useCallback(
    (_, folderIds) => {
      onChange(folderIds[0]);
    },
    [onChange]
  );

  const fetchStashFolders = useCallback(() => {
    if (items === null) {
      setIsLoading(true);
      dispatch(
        fetchGallections({
          type: 'stash',
          onSuccess: response => {
            setItems(
              response.results
                .filter(result => !result.isHidden)
                .map(result => ({
                  gallery: result,
                  value: `${result.folderId}`,
                  selected: result.folderId === initialValueRef.current,
                  label: result.name,
                }))
            );
            setIsLoading(false);
          },
          onError: () => {
            dispatch(addErrorNotification(t('somethingWentWrong')));
            setIsLoading(false);
          },
        })
      );
    }
  }, [items, dispatch, t]);

  // If there is an initial value, fetch the stash folders on component mount.
  // Otherwise we'll do it when the dropdown is opened.
  useEffect(() => {
    if (initialValueRef.current) {
      fetchStashFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GalleryMultiselect
      className={className}
      listClassName={s['list']}
      id={id}
      label={t('originals.deviationSubmit.form.visibility.stash.label')}
      placeholder={t('originals.deviationSubmit.form.visibility.stash.placeholder')}
      items={items ?? []}
      isLoading={isLoading}
      onChange={handleChange}
      onOpen={fetchStashFolders}
      singleSelect
    />
  );
};
StashFolderSelect.displayName = 'StashFolderSelect';

export default StashFolderSelect;
